// Parallax Configuration
const PARALLAX_CONFIG = {
  DEFAULT_STRENGTH: 16,
  EASE_FACTOR: 0.12,
  ANIMATION_THRESHOLD: 0.1,
  RESIZE_DEBOUNCE: 150,
  SELECTOR: '.parallax',
  ITEM_SELECTOR: '.parallax__item',
  INTERSECTION_THRESHOLD: 0.1,
};

// Parallax Instance Class
class ParallaxInstance {
  constructor(container, config, measureCallback) {
    this.container = container;
    this.item = container.querySelector(config.ITEM_SELECTOR);
    this.config = config;
    this.measureCallback = measureCallback;
    
    if (!this.item) return;

    this.strength = parseFloat(container.dataset.strength) || config.DEFAULT_STRENGTH;
    this.ease = config.EASE_FACTOR;
    
    // State
    this.rect = null;
    this.cx = 0;
    this.cy = 0;
    this.targetX = 0;
    this.targetY = 0;
    this.currentX = 0;
    this.currentY = 0;
    this.rafId = null;
    this.isVisible = false;
    
    // Bound methods for event listeners
    this.onMove = this.handleMouseMove.bind(this);
    this.onLeave = this.handleMouseLeave.bind(this);
    
    this.measure();
    this.attachEvents();
  }

  measure() {
    if (!this.container.isConnected) return;
    
    this.rect = this.container.getBoundingClientRect();
    this.cx = this.rect.left + this.rect.width / 2;
    this.cy = this.rect.top + this.rect.height / 2;
  }

  handleMouseMove(e) {
    if (!this.isVisible || !this.rect) return;

    // Normalize mouse position relative to container center (-1..1)
    const nx = (e.clientX - this.cx) / (this.rect.width / 2);
    const ny = (e.clientY - this.cy) / (this.rect.height / 2);

    // Clamp and invert direction
    const clampedX = Math.max(-1, Math.min(1, nx));
    const clampedY = Math.max(-1, Math.min(1, ny));
    this.targetX = -clampedX * this.strength;
    this.targetY = -clampedY * this.strength;

    this.startAnimation();
  }

  handleMouseLeave() {
    this.targetX = 0;
    this.targetY = 0;
    this.startAnimation();
  }

  tick() {
    if (!this.isVisible) {
      this.rafId = null;
      return;
    }

    // Lerp towards target
    this.currentX += (this.targetX - this.currentX) * this.ease;
    this.currentY += (this.targetY - this.currentY) * this.ease;

    this.item.style.transform = `translate3d(${this.currentX}px, ${this.currentY}px, 0)`;

    // Continue animating until close to target
    const threshold = this.config.ANIMATION_THRESHOLD;
    if (
      Math.abs(this.currentX - this.targetX) > threshold ||
      Math.abs(this.currentY - this.targetY) > threshold
    ) {
      this.rafId = requestAnimationFrame(() => this.tick());
    } else {
      // Snap to final position
      this.item.style.transform = `translate3d(${this.targetX}px, ${this.targetY}px, 0)`;
      this.rafId = null;
    }
  }

  startAnimation() {
    if (!this.rafId && this.isVisible) {
      this.rafId = requestAnimationFrame(() => this.tick());
    }
  }

  setVisibility(isVisible) {
    this.isVisible = isVisible;
    
    if (!isVisible) {
      // Stop animation and reset when not visible
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    } else {
      // Remeasure when becoming visible
      this.measure();
    }
  }

  attachEvents() {
    this.container.addEventListener('mousemove', this.onMove);
    this.container.addEventListener('mouseleave', this.onLeave);
  }

  destroy() {
    // Cancel animation
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Remove event listeners
    this.container.removeEventListener('mousemove', this.onMove);
    this.container.removeEventListener('mouseleave', this.onLeave);

    // Reset transform
    if (this.item) {
      this.item.style.transform = '';
    }

    // Clear references
    this.container = null;
    this.item = null;
  }
}

// Parallax Manager Class
class ParallaxManager {
  constructor(config = PARALLAX_CONFIG) {
    this.config = config;
    this.instances = new Map();
    this.observer = null;
    this.resizeTimeoutId = null;
    this.initialized = false;
    
    // Bound methods
    this.handleResize = this.debounceResize.bind(this);
  }

  initialize() {
    if (this.initialized) {
      console.warn('etxParallax already initialized');
      return;
    }

    // console.log('etxParallax initialised');

    // Check for reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // console.log('etxParallax: reduced motion detected, skipping initialization');
      return;
    }

    // Create intersection observer
    this.setupIntersectionObserver();

    // Initialize all parallax containers
    const containers = document.querySelectorAll(this.config.SELECTOR);
    containers.forEach(container => this.addContainer(container));

    // Listen for resize events
    window.addEventListener('resize', this.handleResize, { passive: true });

    this.initialized = true;
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '50px',
      threshold: this.config.INTERSECTION_THRESHOLD,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const instance = this.instances.get(entry.target);
        if (instance) {
          instance.setVisibility(entry.isIntersecting);
        }
      });
    }, options);
  }

  addContainer(container) {
    if (this.instances.has(container)) return;

    const instance = new ParallaxInstance(
      container,
      this.config,
      () => this.measureAll()
    );

    if (instance.item) {
      this.instances.set(container, instance);
      this.observer?.observe(container);
    }
  }

  removeContainer(container) {
    const instance = this.instances.get(container);
    if (instance) {
      instance.destroy();
      this.instances.delete(container);
      this.observer?.unobserve(container);
    }
  }

  measureAll() {
    this.instances.forEach(instance => instance.measure());
  }

  debounceResize() {
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
    }

    this.resizeTimeoutId = setTimeout(() => {
      this.measureAll();
      this.resizeTimeoutId = null;
    }, this.config.RESIZE_DEBOUNCE);
  }

  destroy() {
    // Destroy all instances
    this.instances.forEach(instance => instance.destroy());
    this.instances.clear();

    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);

    // Clear timeout
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
      this.resizeTimeoutId = null;
    }

    this.initialized = false;
    // console.log('etxParallax destroyed');
  }
}

// Main initialization function
function etxParallax() {
  // Use singleton pattern
  if (!window.etxParallaxManager) {
    window.etxParallaxManager = new ParallaxManager();
  }

  window.etxParallaxManager.initialize();
  
  return window.etxParallaxManager;
}

// Export for external use
window.etxParallax = etxParallax;